import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../../../../components/recharts/functions";
import { ReMixChart } from "../../../../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../../../../models/chart_model";
import { renderHourTick } from "../../../../../../components/recharts/components";
import { R_CHART_TYPES } from "../../../../const";
import { getMonthFromDate } from "../../../../../../extensions/date";
import ToggleTabs from "../../../utility/ChartTableToggle";
import SimulationSummaryVolume from "../SummaryChart/SimulationSummaryVolume";

const SelectionOptions = [
  { id: "surplus", label: "Surplus", color: "#20B2AA" },
  { id: "deficit", label: "Deficit", color: "#F1935C" },
];

const SimulatedOptions = [
  { id: "simulated", label: "Simulated" },
  { id: "optimal", label: "Optimal" },
];

interface SurplusDeficitData {
  base_surplus_mw?: number;
  dam_sell_mw?: number;
  base_surplus_rs?: number;
  base_deficit_mw?: number;
  dam_purchase_mw?: number;
  base_deficit_rs?: number;
  total_offer_mw?: number;
  total_offer_rs?: number;
  net_cost?: number;
  net_revenue?: number;
  dam_sell_rs?: number;
  dam_purchase_rs?: number;
  offers?: {
    [key: string]: any;
  };
  [key: string]: any;
}

interface SimulationOptimalData {
  surplus: SurplusDeficitData | null;
  deficit: SurplusDeficitData | null;
}

interface DetailedData {
  date: string;
  slot: number;
  simulated?: SimulationOptimalData;
  optimal?: SimulationOptimalData;
  [key: string]: any;
}

const Intervals = [
  {
    label: "Time Slot",
    key: "time",
    values: ["slot", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

function generateShades(hex: string, count = 10) {
  const toRgb = (hex: string) => {
    hex = hex.replace("#", "");
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  };

  const toHex = ({ r, g, b }: { r: number; g: number; b: number }) =>
    `#${[r, g, b]
      .map((v) =>
        Math.min(255, Math.max(0, Math.round(v)))
          .toString(16)
          .padStart(2, "0"),
      )
      .join("")}`;

  const base = toRgb(hex);

  return Array.from({ length: count }, (_, i) => {
    // Dark → Light factor (0.8 → 1.0)
    const factor = 0.8 + i * (0.2 / (count - 1));

    return toHex({
      r: base.r * factor,
      g: base.g * factor,
      b: base.b * factor,
    });
  });
}


const CHART_COLORS = {
  simulated: {
    damSell: "#005D43",
    baseSurplus: "#9C4500",
    baseDeficit: "#740D33",
    damPurchase: "#C02C63",
    offers: generateShades("#55C8A7"),
  },
  optimal: {
    damSell: "#8B7A00",
    baseSurplus: "#9C4500",
    baseDeficit: "#740D33",
    damPurchase: "#3E346D",
    offers: generateShades("#FFEC61"),
  },
};

function SurplusDeficitSelector({
  selected,
  onSelectionChange,
}: {
  selected: string[];
  onSelectionChange: (selected: string[]) => void;
}) {
  const toggleSelection = (id: string) => {
    if (selected.includes(id)) {
      if (selected.length > 1) {
        onSelectionChange(selected.filter((s) => s !== id));
      }
    } else {
      onSelectionChange([...selected, id]);
    }
  };

  return (
    <div className="flex items-center gap-3">
      {SelectionOptions.map((option) => {
        const isSelected = selected.includes(option.id);
        return (
          <button
            key={option.id}
            onClick={() => toggleSelection(option.id)}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <div
              className={`w-3 h-3 rounded border-2 flex items-center justify-center transition-all ${
                isSelected
                  ? "bg-[#34656D] border-[#34656D]"
                  : "bg-white border-gray-300"
              }`}
            ></div>
            <span className="text-xs font-regular text-[#6F6D7C]">
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function SimulatedOptimalSelector({
  selected,
  onSelectionChange,
}: {
  selected: string[];
  onSelectionChange: (selected: string[]) => void;
}) {
  const toggleSelection = (id: string) => {
    if (selected.includes(id)) {
      if (selected.length > 1) {
        onSelectionChange(selected.filter((s) => s !== id));
      }
    } else {
      onSelectionChange([...selected, id]);
    }
  };

  return (
    <div className="flex items-center gap-3">
      {SimulatedOptions.map((option) => {
        const isSelected = selected.includes(option.id);
        return (
          <button
            key={option.id}
            onClick={() => toggleSelection(option.id)}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <div
              className={`w-3 h-3 rounded border-2 flex items-center justify-center transition-all ${
                isSelected
                  ? "bg-[#34656D] border-[#34656D]"
                  : "bg-white border-gray-300"
              }`}
            ></div>
            <span className="text-xs font-regular text-[#6F6D7C]">
              {option.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function SimulationVolumeAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  setUniqueTargetDate,
  selectedTargetDate,
  activeChartTab,
  setActiveChartTab,
  tab,
  summaryData,
  detailedData,
  selectedOptions,
  setSelectedOptions,
  selectedSimulatedOptions,
  setSelectedSimulatedOptions,
}: {
  selectedBlock: any[];
  refreshKey: number;
  setError?: any;
  setUniqueTargetDate?: any;
  selectedTargetDate: any[];
  activeChartTab: string;
  setActiveChartTab: (tab: string) => void;
  tab: string[];
  summaryData: any[];
  detailedData: DetailedData[];
  selectedOptions: string[];
  setSelectedOptions: (options: string[]) => void;
  selectedSimulatedOptions: string[];
  setSelectedSimulatedOptions: (options: string[]) => void;
}) {
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.key === "time") {
      return "slot";
    }
    return selectedInterval.key;
  }, [selectedInterval]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  // Extract available offer indices dynamically from nested offers object
  const offerIndices = useMemo(() => {
    const indices = new Set<string>();
    if (detailedData) {
      detailedData.forEach((item) => {
        // Check both simulated and optimal for offer names
        ["simulated", "optimal"].forEach((type) => {
          ["surplus", "deficit"].forEach((side) => {
            const data = (item as any)[type]?.[side];
            if (!data) return;

            // Check in .offers sub-object
            if (data.offers) {
              Object.keys(data.offers).forEach((key) => {
                if (key.endsWith("_quantum_mw") && !key.startsWith("total_")) {
                  indices.add(key.replace("_quantum_mw", ""));
                }
              });
            }
            // Also check directly in the object for versatility
            Object.keys(data).forEach((key) => {
              if (key.endsWith("_quantum_mw") && !key.startsWith("total_")) {
                indices.add(key.replace("_quantum_mw", ""));
              }
            });
          });
        });
      });
    }
    return Array.from(indices).sort();
  }, [detailedData]);

  useEffect(() => {
    if (!detailedData || detailedData.length === 0) {
      setChartData([]);
      setChartKeys([]);
      return;
    }

    const hasSurplus = selectedOptions.includes("surplus");
    const hasDeficit = selectedOptions.includes("deficit");

    let transformedData = detailedData.map((item) => {
      const flatItem: any = {
        date: item.date,
        slot: item.slot,
        month: getMonthFromDate(item.date),
      };

      const processData = (type: "simulated" | "optimal") => {
        const data = item[type];
        if (!data) return;

        const surplus = data.surplus;
        const deficit = data.deficit;

        // DAM Sell (sum of surplus and deficit)
        let damSell = 0;
        if (hasSurplus && surplus) damSell += surplus.dam_sell_mw || 0;
        if (hasDeficit && deficit) damSell += deficit.dam_sell_mw || 0;
        flatItem[`${type}_dam_sell_mw`] = damSell;

        // Base Surplus
        if (hasSurplus && surplus) {
          flatItem[`${type}_base_surplus_mw`] = surplus.base_surplus_mw || 0;
        } else {
          flatItem[`${type}_base_surplus_mw`] = 0;
        }

        // Base Deficit & DAM Purchase
        if (hasDeficit && deficit) {
          flatItem[`${type}_base_deficit_mw`] = deficit.base_deficit_mw || 0;
          flatItem[`${type}_dam_purchase_mw`] = deficit.dam_purchase_mw || 0;
        } else {
          flatItem[`${type}_base_deficit_mw`] = 0;
          flatItem[`${type}_dam_purchase_mw`] = 0;
        }

        // Helper to get offer value from various possible structures
        const getOfferVal = (obj: any, offerName: string) => {
          if (!obj) return 0;
          const quantumKey = `${offerName}_quantum_mw`;
          // Check in .offers sub-object
          if (obj.offers && obj.offers[quantumKey] !== undefined) {
            return Number(obj.offers[quantumKey]) || 0;
          }
          // Check directly in the object
          if (obj[quantumKey] !== undefined) {
            return Number(obj[quantumKey]) || 0;
          }
          return 0;
        };

        // Process individual offers (combined surplus and deficit)
        let sumIndiv = 0;
        offerIndices.forEach((offerName) => {
          let val = 0;
          if (hasSurplus && surplus) val += getOfferVal(surplus, offerName);
          if (hasDeficit && deficit) val += getOfferVal(deficit, offerName);
          flatItem[`${type}_${offerName}_mw`] = val;
          sumIndiv += val;
        });

        // Handle total_offer_mw (combined surplus and deficit)
        const getTotalVal = (obj: any) => {
          if (!obj) return 0;
          let val = obj.total_offer_mw || 0;
          if (obj.offers) {
            val =
              val ||
              obj.offers.total_offer_mw ||
              obj.offers.total_quantum_mw ||
              0;
          }
          return Number(val) || 0;
        };

        let totalVal = 0;
        if (hasSurplus) totalVal += getTotalVal(surplus);
        if (hasDeficit) totalVal += getTotalVal(deficit);

        const remainingTotal = Math.max(0, totalVal - sumIndiv);
        if (remainingTotal > 0.1 || (totalVal > 0 && sumIndiv === 0)) {
          flatItem[`${type}_total_offer_mw`] =
            remainingTotal > 0.1 ? remainingTotal : totalVal;
        }
      };

      processData("simulated");
      processData("optimal");

      return flatItem;
    });

    if (selectedTargetDate && selectedTargetDate.length > 0) {
      const types = selectedTargetDate.map(
        (t: any) => t.value?.split("-").reverse().join("-") ?? t,
      );
      transformedData = transformedData.filter((row) =>
        types.includes(row.date),
      );
    }

    function getKeys() {
      if (selectedInterval.key === "time") {
        return ["slot", "date"];
      } else if (selectedInterval.key === "date") {
        return ["date"];
      }
      return [selectedInterval.values[0]];
    }

    const aggregationKeys: string[] = [];

    [...selectedSimulatedOptions]
      .sort((a, b) => (a === "simulated" ? -1 : 1))
      .forEach((simOpt) => {
        if (hasDeficit) {
          aggregationKeys.push(`${simOpt}_base_deficit_mw`);
          aggregationKeys.push(`${simOpt}_dam_purchase_mw`);
          aggregationKeys.push(`${simOpt}_dam_sell_mw`);
        }

        if (hasSurplus) {
          aggregationKeys.push(`${simOpt}_base_surplus_mw`);
          aggregationKeys.push(`${simOpt}_dam_sell_mw`);
        }

        if (hasSurplus || hasDeficit) {
          offerIndices.forEach((offerName) => {
            aggregationKeys.push(`${simOpt}_${offerName}_mw`);
          });

          // Always add total offer key as it might contain the "remainder" or fallback
          aggregationKeys.push(`${simOpt}_total_offer_mw`);
        }
      });

    const par: ChartDataParams = {
      keys: getKeys(),
      tableKeys: ["slot", "date", "month"],
      groupBy: [""],
      volumeKey: "",
      aggregationKeys: Array.from(new Set(aggregationKeys)),
      averageKeys: [],
      averageDuplicationKeys: [],
      priceKey: "",
      data: transformedData,
    };

    const cData = FormatChartData(par);

    const cleanedChartData = cData.data.map((item: any) => {
      const cleaned = { ...item };
      Object.keys(cleaned).forEach((key) => {
        if (key === "slot" || key === "date" || key === "month") {
          return;
        }

        const value = cleaned[key];
        if (
          value === null ||
          value === undefined ||
          (typeof value === "number" && (isNaN(value) || value === 0))
        ) {
          cleaned[key] = undefined;
        }
      });
      return cleaned;
    });

    setChartData(cleanedChartData);
    setChartKeys(cData.keys);
  }, [
    detailedData,
    selectedBlock,
    selectedInterval,
    selectedTargetDate,
    selectedOptions,
    selectedSimulatedOptions,
    offerIndices,
  ]);

  const hasNonZeroValuesForDataKey = useCallback(
    (dataKey: string) => {
      if (!chartData || chartData.length === 0) return false;

      return chartData.some((item) => {
        const value = item[dataKey];
        if (value === null || value === undefined) return false;

        const numValue = typeof value === "string" ? parseFloat(value) : value;
        return (
          numValue !== 0 &&
          !isNaN(numValue) &&
          isFinite(numValue) &&
          numValue > 0
        );
      });
    },
    [chartData],
  );

  const createChartObject = useCallback(
    (
      key: string,
      suffix: string,
      displayName: string,
      stackSuffix: string | undefined,
      chartType: ChartType,
      unit: string,
      stroke: string,
      fill: string,
      yAxisId: string,
    ) => {
      const dataKey = key ? `${key}_${suffix}` : suffix;
      return {
        dataKey: dataKey,
        name: displayName,
        legendColor: fill === "none" ? stroke : fill,
        type: chartType,
        unit: unit,
        stroke: stroke,
        fill: fill,
        yAxisId: yAxisId,
        stackId: stackSuffix,
      };
    },
    [],
  );

  const chartSuffixes = useMemo(() => {
    const metrics: any[] = [];
    const hasSurplus = selectedOptions.includes("surplus");
    const hasDeficit = selectedOptions.includes("deficit");

    const sortedSimulatedOptions = [...selectedSimulatedOptions].sort(
      (a, b) => {
        if (a === "simulated") return -1;
        if (b === "simulated") return 1;
        return 0;
      },
    );

    sortedSimulatedOptions.forEach((simOpt) => {
      const isSimulated = simOpt === "simulated";
      const prefix = isSimulated ? "Simulated" : "Optimal";

      // Deficit metrics
      if (hasDeficit) {
        // Base deficit (main stack)
        metrics.push({
          suffix: `${simOpt}_base_deficit_mw`,
          displayName: `${prefix} Base Deficit (MW)`,
          stackSuffix: `${simOpt}_main_stack`,
          stroke: CHART_COLORS.simulated.baseDeficit,
          fill: CHART_COLORS.simulated.baseDeficit,
          chartType: ChartType.Bar,
          unit: "MW",
          yAxisId: "left",
        });

        // DAM Sell (main stack)
        metrics.push({
          suffix: `${simOpt}_dam_sell_mw`,
          displayName: `${prefix} DAM Sell (MW)`,
          stackSuffix: `${simOpt}_main_stack`,
          stroke: isSimulated
            ? CHART_COLORS.simulated.damSell
            : CHART_COLORS.optimal.damSell,
          fill: isSimulated
            ? CHART_COLORS.simulated.damSell
            : CHART_COLORS.optimal.damSell,
          chartType: ChartType.Bar,
          unit: "MW",
          yAxisId: "left",
        });

        // DAM Purchase (external stack for deficit)
        metrics.push({
          suffix: `${simOpt}_dam_purchase_mw`,
          displayName: `${prefix} DAM Purchase (MW)`,
          stackSuffix: `${simOpt}_external_stack`,
          stroke: isSimulated
            ? CHART_COLORS.simulated.damPurchase
            : CHART_COLORS.optimal.damPurchase,
          fill: isSimulated
            ? CHART_COLORS.simulated.damPurchase
            : CHART_COLORS.optimal.damPurchase,
          chartType: ChartType.Bar,
          unit: "MW",
          yAxisId: "left",
        });
      }

      // Surplus metrics
      if (hasSurplus) {
        // Base surplus (external stack)
        metrics.push({
          suffix: `${simOpt}_base_surplus_mw`,
          displayName: `${prefix} Base Surplus (MW)`,
          stackSuffix: `${simOpt}_external_stack`,
          stroke: CHART_COLORS.simulated.baseSurplus,
          fill: CHART_COLORS.simulated.baseSurplus,
          chartType: ChartType.Bar,
          unit: "MW",
          yAxisId: "left",
        });

        // DAM Sell (main stack) - only add if deficit is not selected
        if (!hasDeficit) {
          metrics.push({
            suffix: `${simOpt}_dam_sell_mw`,
            displayName: `${prefix} DAM Sell (MW)`,
            stackSuffix: `${simOpt}_main_stack`,
            stroke: isSimulated
              ? CHART_COLORS.simulated.damSell
              : CHART_COLORS.optimal.damSell,
            fill: isSimulated
              ? CHART_COLORS.simulated.damSell
              : CHART_COLORS.optimal.damSell,
            chartType: ChartType.Bar,
            unit: "MW",
            yAxisId: "left",
          });
        }
      }

      // Combined Offers
      if (hasSurplus || hasDeficit) {
        const offerColors = isSimulated
          ? CHART_COLORS.simulated.offers
          : CHART_COLORS.optimal.offers;

        offerIndices.forEach((offerName, index) => {
          const color = offerColors[index % offerColors.length];
          const displayName = offerName
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");

          metrics.push({
            suffix: `${simOpt}_${offerName}_mw`,
            displayName: `${prefix} ${displayName} (MW)`,
            stackSuffix: `${simOpt}_external_stack`,
            stroke: color,
            fill: color,
            chartType: ChartType.Bar,
            unit: "MW",
            yAxisId: "left",
          });
        });

        // Total Offer (Remainder/Fallback)
        metrics.push({
          suffix: `${simOpt}_total_offer_mw`,
          displayName: `${prefix} Total Offer (MW)`,
          stackSuffix: `${simOpt}_external_stack`,
          stroke: offerColors[offerIndices.length % offerColors.length],
          fill: offerColors[offerIndices.length % offerColors.length],
          chartType: ChartType.Bar,
          unit: "MW",
          yAxisId: "left",
        });
      }
    });

    return metrics;
  }, [selectedOptions, selectedSimulatedOptions, offerIndices]);

  const legends = useMemo(() => {
    if (!chartKeys || chartKeys.length === 0 || chartSuffixes.length === 0) {
      return [];
    }

    const result: any[] = [];

    chartSuffixes.forEach(
      ({
        suffix,
        displayName,
        stackSuffix,
        chartType,
        unit,
        yAxisId,
        stroke,
        fill,
      }) => {
        chartKeys.forEach((key) => {
          const chartObj = createChartObject(
            key,
            suffix,
            displayName,
            stackSuffix,
            chartType,
            unit,
            stroke,
            fill || stroke,
            yAxisId,
          );

          if (hasNonZeroValuesForDataKey(chartObj.dataKey)) {
            result.push(chartObj);
          }
        });
      },
    );

    // Deduplicate legends by name to ensure clean UI
    const uniqueLegends: any[] = [];
    const seenNames = new Set<string>();

    result.forEach((legend: any) => {
      if (!seenNames.has(legend.name)) {
        uniqueLegends.push(legend);
        seenNames.add(legend.name);
      }
    });

    return uniqueLegends;
  }, [chartKeys, chartSuffixes, createChartObject, hasNonZeroValuesForDataKey]);

  function secondDataKey() {
    if (selectedInterval.isTime && selectedBlock.length > 0) {
      return "date";
    }
    if (selectedInterval.isTime) {
      return "date";
    }
    return undefined;
  }

  return (
    <div className="dash-card !border !border-[#DFEAF2] bg-white w-full !min-h-[100%] rounded-2xl !p-0 mt-2">
      <div className="flex flex-wrap justify-between items-center border-b border-[#DFEAF2] mb-2 p-2">
        <div className="flex items-center gap-3">
          <h2 className="chartHeading !text-[#34656D] !text-xs">
            System Balance (Surplus/Deficit)
          </h2>
        </div>
        <div className="flex flex-wrap gap-x-4 justify-end flex-shrink-0 items-center">
          {activeChartTab === "Detailed" && (
            <>
              <SimulatedOptimalSelector
                selected={selectedSimulatedOptions}
                onSelectionChange={setSelectedSimulatedOptions}
              />
              <div className="w-[1px] h-6 bg-[#34656D]"></div>
              <SurplusDeficitSelector
                selected={selectedOptions}
                onSelectionChange={setSelectedOptions}
              />
            </>
          )}
          <ToggleTabs
            tabs={tab}
            activeTab={activeChartTab}
            setActiveTab={setActiveChartTab}
          />
        </div>
      </div>

      {chartData.length === 0 ? (
        <div className="w-full bg-white flex items-center justify-center min-h-[400px]">
          <p className="text-gray-500">No data available</p>
        </div>
      ) : (
        <>
          {activeChartTab === "Detailed" && (
            <ReMixChart
              chartHeight="calc(100% - 70px)"
              data={chartData}
              showBrush={chartData.length > 96 ? true : undefined}
              brushStart={BrushStart.Start}
              isTimeSlot={selectedInterval.isTime}
              onlyTitle={true}
              legends={legends}
              yAxisLabel="MW"
              nameGap={40}
              xDataKey={xDataKey}
              top={10}
              bottom={30}
              xTick={
                selectedInterval.isTime && selectedBlock.length === 0
                  ? (value: any, index: any) => renderHourTick(value, 10)
                  : undefined
              }
              secondXDataKey={secondDataKey()}
              showToolbox={false}
              tooltipFilter={(item: any) => {
                const value = parseFloat(item.value);
                return value !== 0 && !isNaN(value);
              }}
            />
          )}
          {activeChartTab === "Summary" && (
            <SimulationSummaryVolume
              selectedBlock={selectedBlock}
              refreshKey={refreshKey}
              setError={setError}
              setUniqueTargetDate={setUniqueTargetDate}
              selectedTargetDate={selectedTargetDate}
              summaryData={summaryData}
            />
          )}
        </>
      )}
    </div>
  );
}
